import {useReducer, useEffect, useCallback, useState, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCenter} from '@dnd-kit/core'
import type {DragEndEvent, DragStartEvent} from '@dnd-kit/core'
import {useFormLayoutFull} from '@/hooks/useFormLayout'
import {useFormLayoutPalette} from '@/hooks/useFormLayoutPalette'
import {useAuth} from '@/contexts/auth-context'
import {useServices} from '@/services/context'
import {permissions} from '@/lib/permissions'
import {routes} from '@/lib/routes'
import {useI18n} from '@/i18n'
import {MutedText} from '@/components/shared/MutedText'
import {InlineRow} from '@/components/shared/InlineRow'
import {PageLayout} from '@/components/shared/PageLayout'
import {useToast} from '@/hooks/use-toast'
import {USE_CASE_REGISTRY, resolveFieldLabel, type LayoutField, type LayoutSchema, type UpsertFormLayout} from '@/types/form-layout'
import {createEmptySchema, designerReducer, isDirty} from '@/components/form-layout/designer-reducer'
import {useLayoutReorder} from '@/features/form-layouts/hooks/useLayoutReorder'
import {handleLayoutDragEnd} from '@/features/form-layouts/hooks/handleLayoutDragEnd'
import {DesignerToolbar} from '@/components/form-layout/designer/DesignerToolbar'
import {FieldPalette} from '@/components/form-layout/designer/FieldPalette'
import {LayoutCanvas} from '@/components/form-layout/designer/LayoutCanvas'
import {PropertyPanel} from '@/components/form-layout/designer/PropertyPanel'
import {PreviewPane} from '@/components/form-layout/designer/PreviewPane'
import {
    DesignerFrame,
    DesignerInfoBar,
    DesignerWorkspace,
    DesignerPaletteColumn,
    DesignerCanvasColumn,
    DesignerActionBar,
} from '@/components/form-layout/designer/DesignerFrame'
import {CONCEPT_ICON} from '@/lib/concepts'
import {DragPreview} from '@/components/shared/DragPreview'
import {ExternalLink} from '@/components/shared/ExternalLink'
import {Button} from '@/components/ui/button'
import {ApiError} from '@/api/client'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import type {DesignerSelection} from '@/types/form-layout'

export function FormLayoutDesignerPage() {
    const {useCaseKey = ''} = useParams<{useCaseKey: string}>()
    const navigate = useNavigate()
    const i18n = useI18n()
    const {hasPermission} = useAuth()
    const services = useServices()
    const {toast} = useToast()
    const queryClient = useQueryClient()

    const canManage = hasPermission(permissions.formLayouts.manage)
    const useCaseDef = USE_CASE_REGISTRY.find((uc) => uc.useCaseKey === useCaseKey)
    const displayName = useCaseDef?.displayName ?? useCaseKey

    // Load saved layout
    const {data: savedLayout, isLoading} = useFormLayoutFull(useCaseKey)
    const savedSchema = savedLayout?.layoutSchema ?? null
    const {palette} = useFormLayoutPalette(useCaseKey)

    // Designer state
    const [draft, dispatch] = useReducer(designerReducer, createEmptySchema())
    const [initialized, setInitialized] = useState(false)
    const [savedVersion, setSavedVersion] = useState<number | undefined>(undefined)
    const [previewActive, setPreviewActive] = useState(false)
    const [activeToggle, setActiveToggle] = useState(true)
    const [selection, setSelection] = useState<DesignerSelection>(null)

    // Conflict dialog state
    const [conflictInfo, setConflictInfo] = useState<{updatedBy: string; updatedAt: string} | null>(null)

    // Unsaved changes dialog (manual nav guard — useBlocker requires data router)
    const navGuardDialog = useDialog()

    // Dynamic height: fill from designer top to viewport bottom
    const designerRef = useRef<HTMLDivElement>(null)
    const [designerHeight, setDesignerHeight] = useState<string>('80vh')
    useEffect(() => {
        const measure = () => {
            if (designerRef.current) {
                const top = designerRef.current.getBoundingClientRect().top
                const reserve = window.innerHeight * 0.05
                setDesignerHeight(`${window.innerHeight - top - reserve}px`) // eslint-disable-line i18next/no-literal-string
            }
        }
        measure()
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [initialized])

    // DnD state (lifted from LayoutCanvas so DndContext wraps both palette and canvas)
    const [activeField, setActiveField] = useState<LayoutField | null>(null)
    const dndSensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 5}})
    )

    const layoutReorder = useLayoutReorder({
        useCaseKey,
        displayName,
        active: activeToggle,
        version: savedVersion ?? null,
        dispatch,
    })

    const dragBaselineRef = useRef<LayoutSchema | null>(null)

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current
        if (data?.type === 'palette-item') {
            setActiveField(data.field as LayoutField)
        }
        dragBaselineRef.current = draft
        layoutReorder.captureBaseline(draft)
    }, [draft, layoutReorder])

    const handleDragEnd = useCallback(
        (event: DragEndEvent) => {
            handleLayoutDragEnd({
                event,
                draft,
                palette,
                baseline: dragBaselineRef.current ?? draft,
                dispatch,
                onReorder: layoutReorder.reorder,
                onActiveFieldClear: () => setActiveField(null),
                onDuplicateField: (label) => toast({description: i18n.formLayouts.duplicateFieldToast({label})}),
            })
        },
        [draft, dispatch, toast, i18n, palette, layoutReorder.reorder],
    )

    // Initialize draft from saved layout
    useEffect(() => {
        if (!isLoading && !initialized) {
            if (savedLayout) {
                dispatch({type: 'RESET', schema: savedLayout.layoutSchema})
                setSavedVersion(savedLayout.version)
                setActiveToggle(savedLayout.active)
            }
            setInitialized(true)
        }
    }, [isLoading, initialized, savedLayout])

    const dirty = initialized && isDirty(draft, savedSchema)

    // beforeunload guard
    useEffect(() => {
        if (!dirty) return
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            e.returnValue = ''
        }
        window.addEventListener('beforeunload', handler)
        return () => window.removeEventListener('beforeunload', handler)
    }, [dirty])


    // Save validation
    const validateBeforeSave = useCallback((): boolean => {
        if (draft.sections.length === 0) {
            toast({description: i18n.formLayouts.validationEmptySections})
            return false
        }
        for (const section of draft.sections) {
            if (!section.label.trim()) {
                toast({description: i18n.formLayouts.validationEmptySectionLabel})
                return false
            }
        }
        return true
    }, [draft, toast, i18n])

    // Save mutation
    const saveMutation = useEntityMutation({
        mutationFn: (request: UpsertFormLayout) => services.formLayouts.upsert(useCaseKey, request),
        invalidate: [
            ['form-layouts'],
            ['form-layouts', useCaseKey],
        ],
        successMessage: i18n.formLayouts.saveSuccess,
        onSuccess: (result) => {
            setSavedVersion(result.version)
        },
        onError: (err) => {
            if (err instanceof ApiError && err.status === 409) {
                // Parse conflict info from error body
                let updatedBy = i18n.common.unknown
                let updatedAt = ''
                try {
                    const body = JSON.parse(err.errorDetail ?? '{}') as {updatedBy?: string; updatedAt?: string}
                    updatedBy = body.updatedBy ?? i18n.common.unknown
                    updatedAt = body.updatedAt ?? ''
                 
                } catch {
                    // parse failure uses defaults (unknown user, empty timestamp)
                }
                setConflictInfo({updatedBy, updatedAt})
            }
        },
    })

    const handleSave = useCallback(() => {
        if (!validateBeforeSave()) return
        saveMutation.mutate({
            displayName,
            layoutSchema: draft,
            active: activeToggle,
            version: savedVersion ?? null,
        })
    }, [validateBeforeSave, saveMutation, displayName, draft, activeToggle, savedVersion])

    const handleCancel = useCallback(() => {
        navGuardDialog.open()
    }, [navGuardDialog])

    const guardedNavigateBack = useCallback(() => {
        if (dirty) {
            navGuardDialog.open()
        } else {
            navigate(routes.schema.formLayouts.list())
        }
    }, [dirty, navigate, navGuardDialog])

    if (!canManage) {
        return (
            <PageLayout data-testid="form-layout-designer-page" variant="detail" icon={CONCEPT_ICON.formLayout} title={displayName} backTo={routes.schema.formLayouts.list()}>
                <MutedText>{i18n.formLayouts.noPermission}</MutedText>
            </PageLayout>
        )
    }

    return (
        <PageLayout
            data-testid="form-layout-designer-page"
            variant="detail"
            icon={CONCEPT_ICON.formLayout}
            title={displayName}
            backTo={true}
            onBack={guardedNavigateBack}
            loading={isLoading || !initialized}
        >
        <DesignerFrame innerRef={designerRef} height={designerHeight}>
            {/* Toolbar */}
            <DesignerToolbar
                useCaseKey={useCaseKey}
                displayName={displayName}
                active={activeToggle}
                onActiveChange={setActiveToggle}
                previewActive={previewActive}
                onPreviewToggle={() => setPreviewActive((v) => !v)}
            />

            {/* Used-on info bar */}
            {useCaseDef && (
                <DesignerInfoBar>
                    <MutedText size="xs">{i18n.formLayouts.usedOnPage}</MutedText>
                    <MutedText size="xs">{i18n.formLayouts[useCaseDef.wizardStepKey as keyof typeof i18n.formLayouts] as string}</MutedText>
                    <ExternalLink
                        href={useCaseDef.usedOnPath}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="used-on-page-link"
                    >
                        {i18n.formLayouts.openPage} ↗
                    </ExternalLink>
                </DesignerInfoBar>
            )}

            {/* Main workspace */}
            {previewActive ? (
                <PreviewPane
                    schema={draft}
                    useCaseKey={useCaseKey}
                    palette={palette}
                    onExitPreview={() => setPreviewActive(false)}
                />
            ) : (
                <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <DesignerWorkspace>
                        {/* Palette */}
                        <DesignerPaletteColumn>
                            <FieldPalette
                                useCaseKey={useCaseKey}
                                schema={draft}
                            />
                        </DesignerPaletteColumn>

                        {/* Canvas + Property Panel */}
                        <DesignerCanvasColumn>
                            <LayoutCanvas
                                schema={draft}
                                dispatch={dispatch}
                                selection={selection}
                                onSelect={setSelection}
                                palette={palette}
                            />

                            {/* Property Panel — inside canvas area */}
                            <PropertyPanel
                                schema={draft}
                                selection={selection}
                                dispatch={dispatch}
                                useCaseKey={useCaseKey}
                                onClose={() => setSelection(null)}
                            />
                        </DesignerCanvasColumn>
                    </DesignerWorkspace>

                    {/* Drag overlay */}
                    <DragOverlay>
                        {activeField ? (
                            <DragPreview>
                                {resolveFieldLabel(activeField, palette)}
                            </DragPreview>
                        ) : null}
                    </DragOverlay>
                </DndContext>
            )}

            {/* Fixed bottom bar — Cancel / Save */}
            {!previewActive && (
                <DesignerActionBar>
                    <InlineRow justify="end">
                        <Button
                            variant="outline"
                            onClick={handleCancel}
                            data-testid="designer-cancel-btn"
                        >
                            {i18n.common.cancel}
                        </Button>
                        <Button
                            onClick={handleSave}
                            disabled={saveMutation.isPending}
                            data-testid="designer-save-btn"
                        >
                            {saveMutation.isPending ? i18n.common.saving : i18n.common.save}
                        </Button>
                    </InlineRow>
                </DesignerActionBar>
            )}

            {/* Cancel confirmation dialog */}
            <AlertDialog open={navGuardDialog.isOpen} onOpenChange={(open) => !open && navGuardDialog.close()}>
                <AlertDialogContent data-testid="unsaved-changes-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.formLayouts.cancelDialogTitle}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {dirty
                                ? i18n.formLayouts.cancelDialogBodyDirty
                                : i18n.formLayouts.cancelDialogBody}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={navGuardDialog.close} data-testid="unsaved-changes-stay-btn">
                            {i18n.formLayouts.unsavedChangesStay}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            variant="destructive"
                            data-testid="unsaved-changes-leave-btn"
                            onClick={() => {
                                navGuardDialog.close()
                                navigate(routes.schema.formLayouts.list())
                            }}
                        >
                            {i18n.formLayouts.unsavedChangesLeave}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Conflict dialog */}
            <AlertDialog open={conflictInfo !== null} onOpenChange={(open) => !open && setConflictInfo(null)}>
                <AlertDialogContent data-testid="conflict-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{i18n.formLayouts.conflictTitle}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {i18n.formLayouts.conflictBody({
                                updatedBy: conflictInfo?.updatedBy ?? '',
                                updatedAt: conflictInfo?.updatedAt ?? '',
                            })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setConflictInfo(null)} data-testid="conflict-keep-editing-btn">
                            {i18n.formLayouts.conflictKeepEditing}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            variant="destructive"
                            data-testid="conflict-discard-btn"
                            onClick={() => {
                                setConflictInfo(null)
                                void queryClient.invalidateQueries({queryKey: ['form-layouts', useCaseKey]})
                                navigate(routes.schema.formLayouts.detail(useCaseKey))
                            }}
                        >
                            {i18n.formLayouts.conflictDiscard}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </DesignerFrame>
        </PageLayout>
    )
}
